import { create } from 'zustand'

import { reportsApi } from '@/api/reports.api'
import { mapApiToUi } from '@/store/reports.store'
import type { Report as UiReport } from '@/types/report.types'

interface DashboardKPIs {
  totalReports: number;
  pendingReports: number;
  approvedReports: number;
  totalPayout: number;
}

interface DashboardStore {
  recentReports: UiReport[];
  kpis: DashboardKPIs;
  isLoading: boolean;
  error: string | null;

  fetchDashboardData: () => Promise<void>;
  setKPIs: (kpis: Partial<DashboardKPIs>) => void;
}

export const useDashboardStore = create<DashboardStore>(set => ({
  recentReports: [],
  kpis: {
    totalReports: 0,
    pendingReports: 0,
    approvedReports: 0,
    totalPayout: 0,
  },
  isLoading: false,
  error: null,

  fetchDashboardData: async () => {
    set({ isLoading: true, error: null })
    try {
      // Fetch recent 5 reports sorted by updatedAt
      const recentResponse = await reportsApi.getReports({
        limit: 5,
        // sort: ['u_at:desc'],
      })

      // Fetch all reports to calculate KPIs (since no stats endpoint)
      // Note: In a real app, this should be a specialized stats endpoint
      const allResponse = await reportsApi.getReports({ limit: 1000 })

      const recentReports = recentResponse.Reports.map(mapApiToUi)
      const allReports = allResponse.Reports.map(mapApiToUi)

      const totalPayout = allReports.reduce((s, r) => s + (r.totalGrossPLN || 0), 0)
      const pendingReports = allReports.filter(r => r.status === 'pending').length
      const approvedReports = allReports.filter(r => r.status === 'approved').length

      set({
        recentReports,
        kpis: {
          totalReports: allReports.length,
          pendingReports,
          approvedReports,
          totalPayout,
        },
        isLoading: false,
      })
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
    }
  },

  setKPIs: kpis => set(state => ({
    kpis: { ...state.kpis, ...kpis }
  })),
}))
